import React from 'react';
import { isLogged } from '@food/auth/build/dist/logic/auth';
import { GainViewChecker, TVisibilityCond, VisibilityChecker } from '../types';
import gql from 'graphql-tag';
import * as Sentry from '@sentry/browser';
import { openRenewModal } from '@food/auth/build/dist/utils/renewModal';
import { Button, layouts } from '@food/ui/build/dist/src/Button';
import {
	CampaignMember,
	UserAchievement,
	UserRole,
	VisibilityType,
} from '../server-types';
import { OpenArguments } from '@food/auth/build/dist/types';
import { useTranslation } from 'react-i18next';
import { IFNTheme, registerClass } from '@food/css-manager/build/dist';
import { PardotForm } from '../components/PardotForm';
import { additionalUserDataQuery } from '../components/UserExtendedData';
import { TypeformModalForm } from '../components/TypeformModalForm';
import { hash, hasLocalAchievement } from './misc';
import { client } from '../components/apps/ReservedContent';
import { useHistory } from 'react-router';
import { User } from '@sentry/browser';

export const currentUserRoleQuery = gql`
	query CurrentUserRoleQuery {
		currentUser {
			id
			role
		}
	}
`;

export const currentUserAchievements = gql`
	query CurrentUserAchievementsQuery {
		currentUser {
			id
			userAchievements {
				id
				achievement
			}
		}
	}
`;
export type UserAchievementsDataType = Pick<User, 'id'> & {
	userAchievements: Pick<UserAchievement, 'id' | 'achievement'>[];
};

/**
 * Checker per i diritti di visualizzazione contenuto basato su utente
 * @param cond: UserVisibilityCond
 * @return Promise<boolean>
 */
export const userVisibilityCheck = async (cond: TVisibilityCond): Promise<boolean> => {
	if (cond.type !== VisibilityType.User) {
		throw new Error('ERRORE'); // TODO
	}
	try {
		// in ogni caso, deve essere loggato se questa condizione e' presente
		if (!isLogged()) {
			return false;
		}

		// prendo i dati dell'utente per controllare la validita' della chiave e per eventuali controlli sul ruolo
		const userData = await client.query({
			query: currentUserRoleQuery,
			errorPolicy: 'all',
		});

		// se da questa query ricevo un errore probabilmente ho un token scaduto, quindi non devo avviare necessariamente
		// l'handler di accesso ottenuto
		if (userData.errors) {
			return false;
		}

		// nel caso siano specificati ruoli specifici non e' sufficiente essere loggati
		if (cond.roles && cond.roles.length > 0) {
			return cond.roles.indexOf(userData.data.currentUser.role) !== -1;
		} else {
			return true;
		}
	} catch (e) {
		// mi capita in caso di logout mentre sto facendo renew, semplicemente dico di no
		return false;
	}
};

/**
 * Checker per i diritti di visualizzazione contenuto basato su typeform. Richiede comunque il login?
 * @param cond: TypeformVisibilityCond
 * @return Promise<boolean>
 */
export const typeformVisibilityCheck = async (
	cond: TVisibilityCond,
): Promise<boolean> => {
	// controllo questo caso solo per limiti di mia struttura / typescript
	if (cond.type !== VisibilityType.Typeform) {
		throw new Error('ERRORACCIO'); // TODO
	}

	// potrei avere gia' l'informazione su LocalStorage, controllo per potermi eventualmente evitare altre operazioni
	// piu' onerose e magari asincrone
	if (hasLocalAchievement(cond.achievement)) {
		return true;
	}

	// potrebbe trattarsi di un utente loggato con l'achievement ottenuto su altro pc/browser, controllo i sue
	// achievement ottenuti da graphql

	// chiaramente se non e' loggato non posso controllare, nego l'accesso alla risorsa
	if (!isLogged()) {
		return false;
	}

	try {
		const userData = await client.query({
			query: currentUserAchievements,
			errorPolicy: 'all',
		});

		// se da questa query ricevo un errore probabilmente ho un token scaduto, quindi non devo avviare necessariamente
		// l'handler di accesso ottenuto
		if (userData.errors) {
			return false;
		}

		const currentAchievements = (userData.data
			.currentUser as UserAchievementsDataType).userAchievements.map(
				(obj) => obj.achievement,
			);

		// se trovo l'achievement fra quelli dell'utente questa condizione risulta vera, quindi consento l'accesso
		return Boolean(currentAchievements.indexOf(cond.achievement) !== -1);
	} catch (e) {
		// mi capita in caso di logout mentre sto facendo renew, nego l'accesso alla risorsa
		return false;
	}
};

/**
 * Generatore di checker per i diritti di visualizzazione contenuto basato su campagna salesforce
 * il boolean serve per cambiare leggermente il controllo a seconda del caso in cui sia richiesta la
 * presenza o meno all'evento
 * @param _needPresence: boolean
 */
export const sfCampaignVisibilityCheckGenerator = (_needPresence: boolean) => async (
	cond: TVisibilityCond,
) => {
	if (cond.type !== 'SF_CAMPAIGN') {
		throw new Error('ERRORE'); // TODO
	}
	try {
		// in ogni caso, deve essere loggato se questa condizione e' presente
		if (!isLogged()) {
			return false;
		}

		// prendo i dati dell'utente per controllare la validita' della chiave e per eventuali controlli sul ruolo
		const userData = await client.query({
			query: additionalUserDataQuery,
			errorPolicy: 'all',
		});

		// se da questa query ricevo un errore probabilmente ho un token scaduto, quindi non devo avviare necessariamente
		// l'handler di accesso ottenuto
		if (userData.errors) {
			return false;
		}

		const user = userData.data.currentUser;
		return (
			Array.isArray(user.campaignMemberships) &&
			user.campaignMemberships.some(
				(c: CampaignMember) => c && c.CampaignId === cond.item,
			)
		);
	} catch (e) {
		// mi capita in caso di logout mentre sto facendo renew, semplicemente dico di no
		return false;
	}
};

/**
 * Generatore di checker per i diritti di visualizzazione contenuto basato su item salesforce
 * @param cond: SalesforceItemVisibilityCond
 * @return Promise<boolean>
 */
export const sfItemVisibilityCheck = async (cond: TVisibilityCond) => {
	if (cond.type !== VisibilityType.SfItem) {
		throw new Error('ERRORE'); // TODO
	}
	try {
		// in ogni caso, deve essere loggato se questa condizione e' presente
		if (!isLogged()) {
			return false;
		}

		// prendo i dati dell'utente per controllare la validita' della chiave e per eventuali controlli sul ruolo
		const userData = await client.query({
			query: additionalUserDataQuery,
			errorPolicy: 'all',
		});

		// se da questa query ricevo un errore probabilmente ho un token scaduto, quindi non devo avviare necessariamente
		// l'handler di accesso ottenuto
		if (userData.errors) {
			return false;
		}

		const user = userData.data.currentUser;
		return (
			user.Contact &&
			Array.isArray(user.Contact.Items_Active_Access) &&
			user.Contact.Items_Active_Access.indexOf(
				cond.item.replace(/\|/g, '_').replace(/ /g, '_'), // maledetto salesforce
			) !== -1
		);
	} catch (e) {
		// mi capita in caso di logout mentre sto facendo renew, semplicemente dico di no
		return false;
	}
};

const userStyleClass = registerClass(
	(t: IFNTheme) => `
	text-align: right;
	background-color: ${t.colors.grey.light};
	padding: ${t.font.baseSize * t.ratios.xs}px;
	font-size: ${t.ratios.s}em;
	
	> button {
		font-weight: ${t.font.weights.bold};
		margin-left: ${t.ratios.xs / 2}em; // TODO strano bug!!
		
		&:hover {
			text-decoration: underline;
		}
	}
`,
);

const typeformBtnClass = registerClass(
	(t: IFNTheme) => `
	display: flex;
	justify-content: center;
	margin-bottom: ${t.ratios.l}rem;
`,
);

export const checksElements: {
	[key in VisibilityType]?: {
		check: VisibilityChecker;
		canGainAccess: GainViewChecker;
		gainAccessAction: React.FC<{ condition: TVisibilityCond, label?: string }>;
	};
} = {
	USER: {
		check: userVisibilityCheck,
		canGainAccess: async () => {
			if (!isLogged()) {
				return true;
			}
			const userData = await client.query({
				query: currentUserRoleQuery,
				errorPolicy: 'all',
			});
			if (userData.errors) {
				throw userData.errors[0];
			}
			return userData.data.currentUser.role === UserRole.Consumer;
		},
		gainAccessAction: ({ condition, label }) => {
			// eslint-disable-next-line react-hooks/rules-of-hooks
			const { t } = useTranslation();

			if (condition.type !== 'USER') {
				Sentry.withScope((scope) => {
					scope.setExtra('condizione', condition);
					Sentry.captureException(new Error('Condizione gestita male'));
				});
				return <section />;
			}
			if (!isLogged()) {
				// sono nel caso in cui posso acquisire il diritto in quanto non loggato
				return (
					<div className={userStyleClass}>
						{t`already_registered`}{' '}
						<Button
							onClick={() => openRenewModal()}
							label={label ? label : t`sign_in`}
							layout={layouts.LINK}
						/>
					</div>
				);
			} else {
				// inutile controllare il ruolo: se sono qui sono gia' passato per il `gainAccess`, quindi so di essere un consumer se sono loggato!
				// cambia il comportamento della modale se il ruolo a cui si puo' aspirare e' solo uno
				const renewModalArgs: OpenArguments = {};
				if (condition.roles?.length === 1) {
					renewModalArgs.instantUpgradeRole = condition.roles[0];
				} else {
					renewModalArgs.upgradeRole = condition.roles;
				}

				return (
					<Button
						onClick={() => openRenewModal(renewModalArgs)}
						label={label ? label : t`upgrade to professional`}
						type={'success'}
						block={true}
					/>
				);
			}
		},
	},
	TYPEFORM: {
		check: typeformVisibilityCheck,
		canGainAccess: async () => true,
		gainAccessAction: ({ condition, label }) => {
			// eslint-disable-next-line react-hooks/rules-of-hooks
			const history = useHistory();
			// eslint-disable-next-line react-hooks/rules-of-hooks
			const { t } = useTranslation();

			if (condition.type !== 'TYPEFORM') {
				throw new Error('ERRORE');
			}

			return (
				<TypeformModalForm
					achievement={condition.achievement}
					onSubmit={() => {
						history.replace('/');
					}}
				>
					{(setModalOpen) => (
						<div className={typeformBtnClass}>
							<Button
								label={label ? label : t`compile_form`}
								onClick={() => setModalOpen(true)}
								type={'success'}
							/>
						</div>
					)}
				</TypeformModalForm>
			);
		},
	},
	PARDOT: {
		check: async (condition: TVisibilityCond) => {
			if (condition.type !== 'PARDOT') {
				throw new Error('ERRORE'); // TODO
			}

			return (
				hasLocalAchievement('pardot:' + hash(condition.url)) ||
				hasLocalAchievement(hash(condition.url))
			);
		},
		canGainAccess: async () => true,
		gainAccessAction: PardotForm,
	},
	SF_ITEM: {
		check: sfItemVisibilityCheck,
		canGainAccess: async () => false,
		gainAccessAction: () => <section />,
	},
	SF_CAMPAIGN: {
		check: sfCampaignVisibilityCheckGenerator(false),
		canGainAccess: async () => false,
		gainAccessAction: () => <section />,
	},
	SF_CAMPAIGN_PRESENTE: {
		check: async () => {
			return false;
		},
		canGainAccess: async () => false,
		gainAccessAction: () => <section />,
	},
};
